import * as AvatarPrimitive from "@radix-ui/react-avatar";
import type { ComponentPropsWithRef } from "react";
import * as React from "react";

import { cn } from "@/lib/utils";

const Avatar = ({
  className,
  ref,
  ...props
}: ComponentPropsWithRef<typeof AvatarPrimitive.Root>) => (
  <AvatarPrimitive.Root
    ref={ref}
    data-slot="avatar"
    className={cn(
      "relative inline-flex h-6 w-6 shrink-0 overflow-hidden rounded-full",
      className,
    )}
    {...props}
  />
);
Avatar.displayName = AvatarPrimitive.Root.displayName;

/** Gradient + text classes per color so Tailwind includes them. */
const fallbackGradientClasses = [
  "bg-linear-to-br from-purple-900 to-purple-600 text-purple-100",
  "bg-linear-to-br from-rose-900 to-rose-600 text-rose-100",
  "bg-linear-to-br from-yellow-900 to-yellow-600 text-yellow-100",
  "bg-linear-to-br from-green-900 to-green-600 text-green-100",
  "bg-linear-to-br from-orange-900 to-orange-600 text-orange-100",
  "bg-linear-to-br from-pink-900 to-pink-600 text-pink-100",
  "bg-linear-to-br from-blue-900 to-blue-600 text-blue-100",
];

function generateNumericHashFromString(inputString: string) {
  let numericHash = 0;
  for (let index = 0; index < inputString.length; index++) {
    const charCode = inputString.charCodeAt(index);
    numericHash = ((numericHash << 5) - numericHash + charCode) | 0;
  }
  return numericHash;
}

const AvatarFallback = ({
  className,
  children,
  ref,
  ...props
}: ComponentPropsWithRef<typeof AvatarPrimitive.Fallback>) => {
  const childrenArray = React.Children.toArray(children);
  const childrenAsString = childrenArray.join("");
  const hash = generateNumericHashFromString(childrenAsString);
  const colorIndex = Math.abs(hash) % fallbackGradientClasses.length;
  const gradientClasses = fallbackGradientClasses[colorIndex];
  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      data-slot="avatar-fallback"
      className={cn(
        "flex h-full w-full items-center justify-center rounded-full text-xs",
        gradientClasses,
        className,
      )}
      {...props}
    >
      {children}
    </AvatarPrimitive.Fallback>
  );
};
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarFallback };
